'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useId, useState, type ReactNode } from 'react';
import { Icon, IconType } from '@/components/Icon';

export interface AccordionItemData {
  id: string | number;
  title: string;
  /** Caller renders `<RichText>`, plain text, or anything else. */
  content: ReactNode;
}

interface AccordionProps {
  items: AccordionItemData[];
  /** Render the items into N columns while keeping ONE shared open item. */
  columns?: 1 | 2 | 3 | 4;
  /**
   * Split the flat `items` list into two independent columns at this index —
   * `items[0..splitAt)` left, `items[splitAt..]` right — instead of letting a
   * grid flow them row-major.
   *
   * The double layout needs each column to read top-to-bottom exactly as
   * authored, with no balancing: a row-major grid would interleave the two
   * lists (A1, B1, A2, B2…), and CSS `columns-2` would re-balance them. The
   * open item is still shared across both columns.
   */
  splitAt?: number;
  /**
   * `separators` (default): flat items divided by horizontal rules, no background.
   * `filled`: each item is its own rounded, padded panel; no separators.
   */
  variant?: 'separators' | 'filled';
  /** Tailwind bg class (from `getSectionColorClass`) — `filled` variant only. */
  itemBackgroundClass?: string;
  className?: string;
  itemClassName?: string;
  /** Index of the item open on first render; default: none open. */
  defaultOpenIndex?: number;
}

/**
 * Shared accordion group: a list of expandable title/content items with
 * **single-open** behaviour (10.03.03.02 AC 2) — opening one item closes any
 * other, including when the items are spread across columns, because the open
 * index is owned here rather than per item.
 *
 * Distinct from {@link Collapsible}, which is a *single* self-contained
 * disclosure owning its own state. Both exist on purpose: a group needs
 * coordination, a lone disclosure does not.
 *
 * The atom takes a ready-made Tailwind background class rather than a
 * `SectionBackgroundColor`, so the CMS enum→class mapping stays in the widget
 * layer and this component is usable by non-CMS callers.
 */

// Static lookup — Tailwind cannot extract interpolated class names.
const COLUMN_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: '',
  2: 'md:grid md:grid-cols-2 md:gap-x-8',
  3: 'md:grid md:grid-cols-3 md:gap-x-8',
  4: 'md:grid md:grid-cols-4 md:gap-x-8',
};

export const Accordion = ({
  items,
  columns = 1,
  splitAt,
  variant = 'separators',
  itemBackgroundClass,
  className,
  itemClassName,
  defaultOpenIndex,
}: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(
    defaultOpenIndex ?? null,
  );
  const idPrefix = useId();
  const filled = variant === 'filled';

  // `filled` panels need breathing room between them; `separators` stay flush
  // so the rules read as one continuous list.
  const stackClass = filled ? 'flex flex-col gap-y-4' : undefined;

  const renderItem = (item: AccordionItemData, index: number) => {
    const open = openIndex === index;
    const contentId = `${idPrefix}-content-${index}`;

    return (
      <div
        key={item.id}
        className={classNames(
          filled
            ? classNames('rounded-2xl px-4', itemBackgroundClass)
            : 'border-card-border border-t',
          itemClassName,
        )}
      >
        <button
          type="button"
          onClick={() => setOpenIndex(open ? null : index)}
          aria-expanded={open}
          aria-controls={contentId}
          className={classNames(
            'focus-visible:ring-primary flex w-full items-center gap-2 py-4 text-left focus-visible:ring-2 focus-visible:outline-none',
            'hover:text-primary transition-colors',
            // The outline marks the clickable title row only, not the expanded
            // body. A transparent border sits on every title at all times so
            // opening an item cannot shift the list; only the colour changes.
            // `my-2` keeps it clear of the item divider above, so the two
            // lines never sit at the same height and read as one double rule.
            'my-2 rounded-xl border border-transparent px-4',
            open && 'border-primary',
          )}
        >
          {/* 16px / 400 in every layout, per the design. */}
          <span className="flex-1 text-base font-normal">{item.title}</span>
          <Icon
            src={IconType.SvgChevronDown}
            size={20}
            className={classNames(
              'shrink-0 transition-transform',
              open ? 'rotate-180' : '',
            )}
          />
        </button>
        {/* Content stays in the DOM when collapsed (AC 5: accessible in both
            states); `hidden` keeps it out of the a11y tree. */}
        <div
          id={contentId}
          role="region"
          hidden={!open}
          aria-hidden={!open}
          className="pb-4"
        >
          {item.content}
        </div>
      </div>
    );
  };

  if (splitAt !== undefined) {
    return (
      <div
        className={classNames('md:grid md:grid-cols-2 md:gap-x-8', className)}
      >
        <div className={stackClass}>
          {items.slice(0, splitAt).map(renderItem)}
        </div>
        <div className={stackClass}>
          {items.slice(splitAt).map((item, i) => renderItem(item, splitAt + i))}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(COLUMN_CLASS[columns], stackClass, className)}>
      {items.map(renderItem)}
    </div>
  );
};
